import { Injectable, OnDestroy, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { AdminApiService } from '@fuse/services/admin-api.service';
import { ALNIdentityAuthService } from 'app/core/auth/aln-identity-auth.service';

// Window event the micro-frontends dispatch to publish the news count, e.g.
// window.dispatchEvent(new CustomEvent('news:count', { detail: 3 }))
export const NEWS_COUNT_EVENT = 'news:count';

// Navigation item the news count is shown on
export const NEWS_COUNT_NAV_ITEM_ID = 'information-center';

@Injectable({
    providedIn: 'root'
})
export class NewsCountService implements OnDestroy {

    /**
     * Latest news count, or null when there is nothing to show.
     *
     * Seeded once from the API on application load, then kept up to date by the
     * 'news:count' window event the micro-frontends fire when the news changes.
     * Both sources are needed: refreshing the news page re-mounts the
     * micro-frontend without reloading the shell, so no second API call is made
     * and the event is the only way the new count reaches the sidebar.
     *
     * Held in a signal rather than pushed onto the navigation item: the event is
     * dispatched from outside Angular's zone, so a plain property write would
     * update the value without ever repainting the sidebar. A signal write
     * schedules its own change detection, so the badge updates regardless of
     * whether a zone tick happens to follow.
     */
    readonly count = signal<number | null>(null);

    private tokenSubscription: Subscription;
    private hasLoaded = false;

    /**
     * Read the count off a 'news:count' event.
     *
     * Kept as a bound field so the same reference can be handed to
     * removeEventListener on destroy.
     */
    private readonly onNewsCount = (event: Event): void => {
        this.setCount((event as CustomEvent).detail);
    };

    constructor(
        private _adminApiService: AdminApiService,
        private _alnIdentityAuthService: ALNIdentityAuthService
    ) {
        // Attached in the constructor so the listener is in place as early as
        // possible - the micro-frontends can publish a count as soon as they mount.
        window.addEventListener(NEWS_COUNT_EVENT, this.onNewsCount);

        // The request needs a bearer token, which the auth interceptor takes from
        // session storage. On a refresh the token is usually already there; on a
        // fresh login it only arrives once the token change fires.
        if (sessionStorage.getItem('token')) {
            this.loadCount();
        }

        this.tokenSubscription = this._alnIdentityAuthService
            .getTokenChangeEvent()
            .subscribe(() => this.loadCount());
    }

    ngOnDestroy(): void {
        window.removeEventListener(NEWS_COUNT_EVENT, this.onNewsCount);

        if (this.tokenSubscription) {
            this.tokenSubscription.unsubscribe();
        }
    }

    /**
     * Fetch the initial count from the Milana central API.
     *
     * Only the first load counts - the token change event can fire again on a
     * silent renew, and from then on the window event is the source of truth.
     *
     * The count is scoped to what's unread since the user's last login, so the
     * last logged-in date is looked up first and passed into the news count call.
     */
    private loadCount(): void {
        if (this.hasLoaded || !this.hasInformationCenterAccess()) {
            return;
        }
        this.hasLoaded = true;

        this._adminApiService.getUserLastLoggedDate().pipe(
            switchMap((lastLoggedDateResponse) =>
                this._adminApiService.getNewsCount(lastLoggedDateResponse ? lastLoggedDateResponse.content : undefined)
            )
        ).subscribe({
            next: (response) => {
                this.setCount(response ? response.content : null);
            },
            error: () => {
                // Let a later token change retry; the badge stays hidden until then.
                this.hasLoaded = false;
            }
        });
    }

    /**
     * Whether the logged-in user has the Information Center permission.
     *
     * Read straight from session storage - the same 'roleAccessPermissions'
     * blob the sidebar itself uses to hide the Information Center nav item -
     * so the badge and the nav item always agree on visibility.
     */
    private hasInformationCenterAccess(): boolean {
        const raw = sessionStorage.getItem('roleAccessPermissions');
        if (!raw) {
            return false;
        }

        try {
            return !!JSON.parse(raw)?.InformationCenterAccess;
        } catch {
            return false;
        }
    }

    /**
     * Apply a count from either source, hiding the badge for anything that isn't
     * a positive number.
     */
    private setCount(value: unknown): void {
        const count = Number(value);

        this.count.set(Number.isFinite(count) && count > 0 ? count : null);
    }
}
